import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { createTool, getSignedUrl, getObjectUrl } from '../../graphql/mutation';
import { getCategoriesAdmin } from '../../graphql/queries';
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  Upload, 
  Button, 
  Space, 
  Row, 
  Col,
  Image,
  InputNumber,
  Divider,
  Progress,
  Alert,
  Radio,
  Switch
} from 'antd';
import { UploadOutlined, PlusOutlined, MinusCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { uploadToR2 } from '../../utils/uploadToR2';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import { compressVideoToLowRes, isFFmpegSupported, getCompressionStatus } from '../../utils/videoCompression';
import './Studios.css';

const { Option } = Select;
const { TextArea } = Input;

const imageTypes = [
  { value: 'DUAL', label: 'Dual' },
  { value: 'SINGLE', label: 'Single' },
];

const toolTypes = [
  { value: 'VIDEO', label: 'Video' },
  { value: 'IMAGE', label: 'Image' },
];

const systemProviders = [
  { value: 'WIRO_PX', label: 'Wiro PX' },
  { value: 'WIRO_KLING', label: 'Wiro Kling' },
  { value: 'WIRO_MINIMAX', label: 'Wiro Minimax' },
  { value: 'WIRO_BYTEDANCE', label: 'Wiro Bytedance' },
  { value: 'FAL', label: 'Fal' },
  { value: 'WIRO_WAN22_5B', label: 'Wiro Wan22 5B' },
  { value: 'WIRO_WAN22_14B', label: 'Wiro Wan22 14B' },
  { value: 'SERVICE', label: 'Service' },
];

const CreateTool = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [files, setFiles] = useState({
    banner: null,
    video: null,
    lowResVideo: null,
    webm: null,
    webp: null,
  });
  const [previews, setPreviews] = useState({
    banner: null,
    video: null,
    lowResVideo: null,
    webm: null,
    webp: null,
  });
  const [loading, setLoading] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);

  const { data: categoriesData } = useQuery(getCategoriesAdmin);

  const [createToolMutation] = useMutation(createTool);
  const [getSignedUrlMutation] = useMutation(getSignedUrl);
  const [getObjectUrlMutation] = useMutation(getObjectUrl);

  const handleFileChange = async (type, file) => {
    if (file) {
      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        showSnackBar('error', 'File Too Large', `${type} must be smaller than 100MB`);
        return false;
      }

      // Validate file types
      if (type === 'banner' && !file.type.startsWith('image/')) {
        showSnackBar('error', 'Invalid File Type', 'Banner must be an image file');
        return false;
      }

      if (type === 'video' && !file.type.startsWith('video/')) {
        showSnackBar('error', 'Invalid File Type', 'Video must be a video file');
        return false;
      }

      if (type === 'webm' && file.type !== 'video/webm') {
        showSnackBar('error', 'Invalid File Type', 'WebM file must be a .webm video file');
        return false;
      }

      if (type === 'webp' && file.type !== 'image/webp') {
        showSnackBar('error', 'Invalid File Type', 'WebP file must be a .webp image file');
        return false;
      }

      setFiles(prev => ({ ...prev, [type]: file }));
      setPreviews(prev => ({ 
        ...prev, 
        [type]: URL.createObjectURL(file) 
      }));
      showSnackBar('success', 'File Selected', `${type} file selected successfully`);

      // Auto-generate low resolution video when video is uploaded
      if (type === 'video' && isFFmpegSupported()) {
        try {
          setIsCompressing(true);
          setCompressionProgress(0);
          showSnackBar('info', 'Compressing Video', 'Generating low resolution version...');
          
          const lowResFile = await compressVideoToLowRes(file, setCompressionProgress);
          
          setFiles(prev => ({ ...prev, lowResVideo: lowResFile }));
          setPreviews(prev => ({ 
            ...prev, 
            lowResVideo: URL.createObjectURL(lowResFile) 
          }));
          
          showSnackBar('success', 'Compression Complete', 'Low resolution video generated successfully');
        } catch (error) {
          console.error('Failed to compress video:', error);
          showSnackBar('warning', 'Compression Failed', 'Could not generate low resolution video. You can still proceed.');
        } finally {
          setIsCompressing(false);
          setCompressionProgress(0);
        }
      }
    }
    return false;
  };

  const getSignedUrls = async () => {
    const urls = {};
    for (const [type, file] of Object.entries(files)) {
      if (file) {
        const { data } = await getSignedUrlMutation({
          variables: { fileName: file.name }
        });
        urls[type] = data.getSignedUrl;
      }
    }
    return urls;
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      if (!files.banner) {
        showSnackBar('error', 'Missing Banner', 'Banner image is required');
        return;
      }

      const signedUrls = await getSignedUrls();
      
      // Upload files - only upload files that exist
      const uploadPromises = Object.entries(signedUrls)
        .filter(([type, urlData]) => files[type] !== null)
        .map(([type, urlData]) => 
          uploadToR2(files[type], urlData.url, urlData.contentType)
        );
      
      if (uploadPromises.length > 0) {
        showSnackBar('info', 'Uploading Files', 'Please wait while files are being uploaded...');
        await Promise.all(uploadPromises);
      }

      // Get permanent URLs - only for files that were uploaded
      const objectUrls = {};
      for (const [type, urlData] of Object.entries(signedUrls)) {
        if (files[type] !== null) {
          const { data } = await getObjectUrlMutation({
            variables: { key: urlData.id, eTag: 'etag' }
          });
          objectUrls[type] = data.getObjectUrl;
        }
      }

      // Filter aspect ratios to only include fields accepted by ToolRatioInput
      const cleanAspectRatios = values.aspectRatios ? values.aspectRatios.map(ratio => ({
        width: ratio.width,
        height: ratio.height,
        name: ratio.name,
        aspectRatioX: ratio.aspectRatioX,
        aspectRatioY: ratio.aspectRatioY,
      })) : [];

      // Create tool with input object
      const input = {
        name: values.name,
        skipTranslation: values.skipTranslation || false,
        imageType: values.imageType,
        bannerUrl: objectUrls.banner,
        videoUrl: objectUrls.video || null,
        lowResolutionUrl: objectUrls.lowResVideo || null,
        webmUrl: objectUrls.webm || null,
        webpUrl: objectUrls.webp || null,
        templateId: values.templateId && values.templateId.trim() !== '' ? values.templateId.trim() : null,
        description:
          values.description?.trim() === ""
            ? null
            : values.description,

        prompt: values.prompt || null,
        isFeatured: values.isFeatured || false,
        isLivePhoto: values.isLivePhoto || false,
        isHidden: values.isHidden || false,
        type: values.type || 'VIDEO',
        duration: values.duration || null,
        cost: values.cost || null,
        actualCost: values.actualCost || null,
        aspectRatios: cleanAspectRatios,
        categoryId: values.categoryId || null,
        provider: values.provider,
        badgeText: values.badgeText || null,
        badgeIcon: values.badgeIcon || null,
        badgeTextColor: values.badgeTextColor || null,
        badgeBackgroundColor: values.badgeBackgroundColor || null,
        keywords: values.keywords || [],
      };

      await createToolMutation({
        variables: { input }
      });

      showSnackBar('success', 'Success', 'Tool created successfully');
      navigate('/tools');
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to create tool');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="studios-container">
      <Card>
        <div className="table-header">
          <h2>Create Tool</h2>
          <Button onClick={() => navigate('/tools')}>
            Back to List
          </Button>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isFeatured: false,
            isLivePhoto: false,
            isHidden: false,
            skipTranslation: false,
            type: 'VIDEO',
            duration: 3,
            aspectRatios: [
              {
                width: 1920,
                height: 1080,
                name: 'landscape',
                aspectRatioX: 16,
                aspectRatioY: 9,
              }
            ],
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Tool Name"
                rules={[{ required: true, message: 'Please enter tool name' }]}
              >
                <Input placeholder="Enter tool name" />
              </Form.Item>

              <Form.Item
                name="skipTranslation"
                label="Translation"
              >
                <Radio.Group>
                  <Radio value={false}>
                    Auto translate 
                  </Radio>
                  <Radio value={true}>
                    Do not translate (use original name)
                  </Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name="imageType"
                label="Image Type"
                rules={[{ required: true, message: 'Please select image type' }]}
              >
                <Select placeholder="Select image type">
                  {imageTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="type"
                label="Tool Type"
                rules={[{ required: true, message: 'Please select tool type' }]}
              >
                <Select placeholder="Select tool type">
                  {toolTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="templateId"
                label="Template ID"
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Enter template ID (optional)"
                />
              </Form.Item>

              <Form.Item
                name="duration"
                label="Duration (seconds)"
              >
                <InputNumber
                  min={1}
                  max={30}
                  style={{ width: '100%' }}
                  placeholder="Enter duration in seconds"
                />
              </Form.Item>

              <Form.Item
                name="cost"
                label="Credit Cost "
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="Auto-calculated"
                  controls={false}
                  disabled
                />
              </Form.Item>
              <Form.Item
                name="actualCost"
                label="Dollar Cost per Generation ($)"
              >
                <InputNumber
                  rules={[{ required: true, message: 'Please enter cost' }]}
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="Enter cost in dollars"
                  controls={false}
                />
              </Form.Item>

              <Form.Item
                name="isFeatured"
                label="Featured"
              >
                <Select placeholder="Is this tool featured?">
                  <Option value={true}>Yes</Option>
                  <Option value={false}>No</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="isHidden"
                label="Hidden"
                valuePropName="checked"
              >
                <Switch checkedChildren="Hidden" unCheckedChildren="Visible" />
              </Form.Item>

              <Form.Item
                name="isLivePhoto"
                label="Live Photo"
              >
                <Select placeholder="Is this a live photo?">
                  <Option value={true}>Yes</Option>
                  <Option value={false}>No</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="categoryId"
                label="Category"
              >
                <Select placeholder="Select category (optional)" allowClear>
                  {categoriesData?.getCategoriesAdmin?.map(category => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="provider"
                label="Provider"
                rules={[{ required: true, message: 'Please select a provider' }]}
              >
                <Select placeholder="Select provider">
                  {systemProviders.map(provider => (
                    <Option key={provider.value} value={provider.value}>
                      {provider.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
              >
                <TextArea 
                  rows={3} 
                  placeholder="Enter tool description (optional)"
                />
              </Form.Item>

              <Form.Item
                name="prompt"
                label="Prompt"
              >
                <TextArea 
                  rows={4} 
                  placeholder="Enter prompt (optional)"
                />
              </Form.Item>

              <Form.Item
                name="keywords"
                label="Keywords"
                tooltip="Words/phrases describing this tool's mood, style, or subject — used for search. Press enter or comma to add."
              >
                <Select
                  mode="tags"
                  tokenSeparators={[',']}
                  style={{ width: '100%' }}
                  placeholder="e.g. night, cinematic, city"
                  open={false}
                />
              </Form.Item>

              <Divider orientation="left">Badge Settings</Divider>

              <Form.Item
                name="badgeText"
                label="Badge Text"
              >
                <Input placeholder="Enter badge text (e.g., 'NEW', 'POPULAR')" />
              </Form.Item>

              <Form.Item
                name="badgeIcon"
                label="Badge Icon"
              >
                <Input placeholder="Enter badge icon name (optional)" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="badgeTextColor"
                    label="Badge Text Color"
                  >
                    <Input 
                      type="color" 
                      style={{ height: 40 }}
                      placeholder="#FFFFFF"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="badgeBackgroundColor"
                    label="Badge Background Color"
                  >
                    <Input 
                      type="color" 
                      style={{ height: 40 }}
                      placeholder="#FF0000"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col span={12}>
              <Form.Item label="Aspect Ratios">
                <Form.List name="aspectRatios">
                  {(fields, { add, remove }) => (
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 16 }}>
                      {fields.map(({ key, name, ...restField }) => (
                        <Row key={key} gutter={8} style={{ marginBottom: 12, alignItems: 'center' }}>
                          <Col span={5}>
                            <Form.Item
                              {...restField}
                              name={[name, 'name']}
                              style={{ marginBottom: 0 }}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <Input placeholder="Name" size="small" />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'width']}
                              style={{ marginBottom: 0 }}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <InputNumber 
                                min={1} 
                                style={{ width: '100%' }}
                                placeholder="W"
                                size="small"
                                controls={false}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={1} style={{ textAlign: 'center', fontSize: 12, color: '#999' }}>
                            ×
                          </Col>
                          <Col span={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'height']}
                              style={{ marginBottom: 0 }}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <InputNumber 
                                min={1} 
                                style={{ width: '100%' }}
                                placeholder="H"
                                size="small"
                                controls={false}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'aspectRatioX']}
                              style={{ marginBottom: 0 }}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <InputNumber 
                                min={1} 
                                style={{ width: '100%' }}
                                placeholder="X"
                                size="small"
                                controls={false}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={1} style={{ textAlign: 'center', fontSize: 12, color: '#999' }}>
                            :
                          </Col>
                          <Col span={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'aspectRatioY']}
                              style={{ marginBottom: 0 }}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <InputNumber 
                                min={1} 
                                style={{ width: '100%' }}
                                placeholder="Y"
                                size="small"
                                controls={false}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={1}>
                            {fields.length > 1 && (
                              <Button
                                type="text"
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                                size="small"
                                danger
                                style={{ color: '#ff4d4f' }}
                              />
                            )}
                          </Col>
                        </Row>
                      ))}
                      
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                        size="small"
                        style={{ marginTop: 8 }}
                      >
                        Add Ratio
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Form.Item>

              <Divider orientation="left">Media Files</Divider>

              <Form.Item label="Banner Image (Required)">
                <Upload
                  beforeUpload={(file) => handleFileChange('banner', file)}
                  accept="image/*"
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>
                    Select Banner Image
                  </Button>
                </Upload>
                {previews.banner && (
                  <div style={{ marginTop: 10 }}>
                    <Image
                      src={previews.banner}
                      width={100}
                      height={60}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                  </div>
                )}
              </Form.Item>

              <Form.Item label="Video File (Optional)">
                <Upload
                  beforeUpload={(file) => handleFileChange('video', file)}
                  accept="video/*"
                  showUploadList={false}
                  disabled={isCompressing}
                >
                  <Button icon={isCompressing ? <LoadingOutlined /> : <UploadOutlined />} disabled={isCompressing}>
                    {isCompressing ? 'Compressing...' : 'Select Video File'}
                  </Button>
                </Upload>
                {isCompressing && (
                  <div style={{ marginTop: 10 }}>
                    <Progress 
                      percent={compressionProgress} 
                      size="small" 
                      status="active"
                      format={() => getCompressionStatus(compressionProgress)}
                    />
                  </div>
                )}
                {previews.video && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Original Video:</div>
                    <video
                      src={previews.video}
                      width={150}
                      height={90}
                      controls
                      style={{ borderRadius: 4 }}
                    />
                  </div>
                )}
                {previews.lowResVideo && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: '#52c41a', marginBottom: 4 }}>Low Resolution (Auto-generated):</div>
                    <video
                      src={previews.lowResVideo}
                      width={150}
                      height={90}
                      controls
                      style={{ borderRadius: 4 }}
                    />
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                      Size: {files.lowResVideo ? `${(files.lowResVideo.size / 1024 / 1024).toFixed(2)} MB` : ''}
                    </div>
                  </div>
                )}
                {!isFFmpegSupported() && (
                  <Alert
                    message="Low resolution video generation not supported in this browser"
                    type="warning"
                    showIcon
                    style={{ marginTop: 10 }}
                  />
                )}
              </Form.Item>

              <Form.Item label="WebM File (Optional)">
                <Upload
                  beforeUpload={(file) => handleFileChange('webm', file)}
                  accept=".webm,video/webm"
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>
                    Select WebM File
                  </Button>
                </Upload>
                {previews.webm && (
                  <div style={{ marginTop: 10 }}>
                    <video
                      src={previews.webm}
                      width={150}
                      height={90}
                      controls
                      style={{ borderRadius: 4 }}
                    />
                  </div>
                )}
              </Form.Item>

              <Form.Item label="WebP Image (Optional)">
                <Upload
                  beforeUpload={(file) => handleFileChange('webp', file)}
                  accept=".webp,image/webp"
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>
                    Select WebP Image
                  </Button>
                </Upload>
                {previews.webp && (
                  <div style={{ marginTop: 10 }}>
                    <Image
                      src={previews.webp}
                      width={100}
                      height={60}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} disabled={isCompressing}>
                Create Tool
              </Button>
              <Button onClick={() => navigate('/tools')} disabled={isCompressing}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CreateTool; 